<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Button from 'primevue/button';
import DatePicker from 'primevue/datepicker';
import Dialog from 'primevue/dialog';
import Tag from 'primevue/tag';
import { button as greenButton } from '@apparatix/theme/Green';
import { useDateFns } from '@apparatix/composables';
import type { OfferManagementRow } from '@apparatix/types/OfferManagement';

type Props = {
  visible: boolean;
  row?: OfferManagementRow;
  canEdit: boolean;
  isSaving: boolean;
}

type Emits = {
  (e: 'close'): void;
  (e: 'on:save', holdThru: string | null): void;
  (e: 'on:open-opportunity'): void;
}

const props = defineProps<Props>();

const emit = defineEmits<Emits>();

const { formatDate, parse } = useDateFns();

const holdThruDate = ref<Date | null>(null);

// Holds dated before today are removed by the hold expiry job (cronKillOldHolds), so only today or later is offered.
const today = new Date(new Date().setHours(0, 0, 0, 0));

const isVisible = computed({
  get: () => props.visible,
  set: () => emit('close'),
});

watch(() => props.visible, visible => {
  if (!visible) return;
  holdThruDate.value = props.row?.holdThru ? parse(props.row.holdThru) ?? null : null;
});

const selectedHoldThru = computed(() => (holdThruDate.value ? formatDate(holdThruDate.value) : null));

const isHoldThruChanged = computed(() => selectedHoldThru.value !== (props.row?.holdThru ?? null));

const hasOpportunity = computed(() => Boolean(props.row?.opportunityId));
</script>
<template>
  <Dialog
    v-model:visible="isVisible"
    header="Offer Hold"
    style="width: 33vw"
    :breakpoints="{ '1280px': '50vw', '960px': '75vw', '640px': '95vw' }"
    modal
  >
    <!-- Dialog focuses the first [autofocus] element on open (else the close button); land on the
         content wrapper so nothing interactive is focused and the date picker behaves normally. -->
    <div
      v-if="row"
      autofocus
      tabindex="-1"
      class="flex flex-col gap-4 outline-none"
    >
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <span class="text-lg font-semibold">{{ row.advertiser }}</span>
          <Tag :value="row.documentStatus" severity="secondary" />
        </div>
        <span class="text-sm text-[var(--black-80)]">Proposal {{ row.proposalId }} &middot; {{ row.documentState }} &middot; Created {{ row.createdDate }} by {{ row.createdBy }}</span>
        <span v-if="row.opportunityName" class="text-sm text-[var(--black-80)]">{{ row.opportunityName }}</span>
        <span class="text-sm" :class="row.hasHoldMismatch ? 'text-red-600' : 'text-[var(--black-80)]'">
          {{ row.holdCount }} of {{ row.faceCount }} faces held
        </span>
      </div>
      <div class="flex flex-col gap-2">
        <label for="offer-hold-thru" class="text-sm font-semibold">Hold Thru</label>
        <DatePicker
          v-if="canEdit"
          v-model="holdThruDate"
          input-id="offer-hold-thru"
          date-format="yy-mm-dd"
          placeholder="No hold"
          show-button-bar
          :min-date="today"
          :disabled="isSaving"
          fluid
        />
        <template v-else>
          <span id="offer-hold-thru">{{ row.holdThru ?? 'No hold' }}</span>
          <p class="text-sm font-normal">
            Offer Management is read-only for your account.
          </p>
        </template>
      </div>
      <div class="flex flex-wrap gap-2 justify-end items-center">
        <span v-tooltip.top="hasOpportunity ? undefined : 'This offer is not linked to a CRM opportunity.'" class="mr-auto">
          <Button
            severity="secondary"
            size="small"
            :disabled="!hasOpportunity"
            @click="emit('on:open-opportunity')"
          >
            Open Opportunity
          </Button>
        </span>
        <template v-if="canEdit">
          <Button
            severity="danger"
            size="small"
            :disabled="isSaving || !row.holdThru"
            @click="emit('on:save', null)"
          >
            Clear Hold
          </Button>
          <Button
            :dt="greenButton"
            size="small"
            :disabled="isSaving || !isHoldThruChanged"
            @click="emit('on:save', selectedHoldThru)"
          >
            Save
          </Button>
        </template>
        <Button
          v-else
          severity="secondary"
          size="small"
          @click="emit('close')"
        >
          Close
        </Button>
      </div>
    </div>
  </Dialog>
</template>
